import React, { type ButtonHTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';

import { YatSprite } from '@pkg/components/YatSprite';
import { useAppDispatch } from '@pkg/hooks';

import { addEmoji } from '@app/store/actions/picker';

import './AddEmojiButton.scss';

interface AddEmojiButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    emoji: string;
    emojiName: string;
    classes?: string;
    large?: boolean;
    keepInputFocus?: boolean;
}

const YatAddEmojiButton = ({
    emoji,
    emojiName,
    classes = '',
    large = false,
    keepInputFocus = false,
    ...props
}: AddEmojiButtonProps) => {
    const isTouch = window.matchMedia('(hover: none)').matches;
    const element = useRef() as React.MutableRefObject<HTMLButtonElement>;
    const [disableAnimation, setDisableAnimation] = useState(true);
    const [debounceClick, setDebounceClick] = useState(false);
    const dispatch = useAppDispatch();

    const onMouseEnter = () => {
        setDisableAnimation(false);
        element.current.removeEventListener('mouseenter', onMouseEnter);
    };

    const pickEmoji = useCallback(() => {
        if (debounceClick) return;
        dispatch(addEmoji([emoji, emojiName]));
        setDebounceClick(true);

        if (keepInputFocus) {
            document.getElementById('pickerFormInput')?.focus();
        }
    }, [dispatch, keepInputFocus, debounceClick, emoji, emojiName]);

    useEffect(() => {
        if (debounceClick) {
            gsap.delayedCall(0.25, setDebounceClick, [false]);
        }
    }, [debounceClick]);

    const classNames = useMemo(() => {
        let classNames = 'add-emoji-button';
        if (large) classNames += ' add-emoji-button--large';
        if (disableAnimation) classNames += ' add-emoji-button--disable-animation';
        if (classes) classNames += ` ${classes}`;

        return classNames;
    }, [large, disableAnimation, classes]);

    return (
        <button
            ref={element}
            className={classNames}
            onMouseEnter={isTouch ? undefined : onMouseEnter}
            onClick={pickEmoji}
            {...props}
        >
            <div className="add-emoji-button__inner">
                <YatSprite emojiID={emoji} className="add-emoji-button__emoji" />
            </div>
        </button>
    );
};

export default YatAddEmojiButton;
